import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

/**
 * Upsert Sales History Transfer
 *
 * Takes ONE history-remap rule as single-value input sockets, shaped after the
 * Gently model (`im_sales_history_transfers`) — not after any source system. A
 * CDC observer or file parser maps its own columns into these sockets, so the
 * same step serves every source; anything source-specific (field naming,
 * sentinel values, chain flattening) belongs in that upstream mapping.
 *
 * The rows this writes are READ-TIME OVERLAYS: the demand readers resolve them
 * when the forecast runs, and nothing here rewrites stored history. A rule can
 * therefore be added, corrected or revoked without a re-ingest, and a wrong rule
 * costs a re-run rather than a repair job.
 *
 * The rule is a product pair (old SKU → new SKU) with an OPTIONAL warehouse pair:
 *
 *  - Both location codes blank, or both naming the same location → a product-only
 *    rule, applying at EVERY warehouse. That is the wildcard the table documents,
 *    not a missing value.
 *  - Two different codes that both resolve → the rule is scoped to that pair.
 *  - One code blank, or a code resolving to no warehouse → the row is SKIPPED.
 *    Half a pair silently widens the rule to every warehouse instead of the one
 *    that was meant, which is worse than not writing it.
 *
 * A resolved pair must sit inside ONE division: the demand readers are
 * division-scoped, so a cross-division rule would move history into a read that
 * never looks at its source. Those rows are skipped, matching what the create API
 * refuses.
 *
 * Every socket is nullable so a malformed row is REPORTED rather than failing the
 * whole chunk. Skip reasons are logged with a bounded sample per reason; the
 * per-row output is `written` / `skipped` only, because nothing downstream of
 * this leaf step branches on the reason.
 *
 * Re-ingest is idempotent: the conflict target is the table's partial unique
 * index on the rule's scope, so a second run updates the share and provenance in
 * place. A REVOKED rule is a tombstone — it sits outside that partial index, so
 * re-ingesting its scope inserts a fresh active row rather than resurrecting the
 * one an operator deliberately switched off.
 */
export const upsertSalesHistoryTransferStep = defineStepConfig({
  type: "upsert_sales_history_transfer",
  name: "Upsert Sales History Transfer",
  description:
    "Upserts one sales-history copy/transfer rule (old SKU -> new SKU, optional warehouse pair, copy percent) into im_sales_history_transfers. Unknown SKUs, half warehouse pairs, cross-division pairs and self-rules are skipped and reported, never created.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    oldProductSku: z
      .string()
      .nullable()
      .optional()
      .describe("Source SKU — the coordinate history is drawn FROM"),
    newProductSku: z
      .string()
      .nullable()
      .optional()
      .describe("Target SKU — the coordinate history is drawn INTO"),
    oldWarehouseCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Origin code of the source warehouse. Blank, or equal to the target code, makes this a product-only rule applying at every warehouse.",
      ),
    newWarehouseCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Origin code of the target warehouse. Supplying only one of the two codes SKIPS the row.",
      ),
    // A `numeric` ingest column arrives as a string and a hand-wired expression
    // as a number, so both are accepted and normalized to text — the source
    // field is 3 characters of free text and the transform, not Zod, decides
    // what an unparseable one means. `preprocess` rather than a union so the
    // socket still infers as `string` (see `quantityInput.ts`).
    copyPercent: z
      .preprocess(
        (value) => (typeof value === "number" ? String(value) : value),
        z.string(),
      )
      .nullable()
      .optional()
      .describe(
        "Share of the source's history the target receives. Blank, zero, malformed or outside 0 < pct <= 100 all mean a full 100%.",
      ),
    gsdoc: z
      .string()
      .nullable()
      .optional()
      .describe("Provenance passthrough, stored verbatim in `metadata.gsdoc`"),
    gsfxmodel: z
      .string()
      .nullable()
      .optional()
      .describe("Provenance passthrough, stored in `metadata.gsfxmodel`"),
    gsupdflag: z
      .string()
      .nullable()
      .optional()
      .describe("Provenance passthrough, stored in `metadata.gsupdflag`"),
    gsonhflag: z
      .string()
      .nullable()
      .optional()
      .describe("Provenance passthrough, stored in `metadata.gsonhflag`"),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The rule was upserted"),
    skipped: z
      .boolean()
      .describe(
        "The row was skipped — the reason (missing or unknown SKU, half a warehouse pair, an unresolved warehouse, a cross-division pair, a self-rule) is logged, not emitted",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    mode: z
      .enum(["copy", "transfer"])
      .prefault("copy")
      .describe(
        "`copy` leaves the source's history intact; `transfer` also REMOVES the share from the source. Leave on `copy` unless the source system is known to mean a move — a wrong `copy` over-counts a forecast, a wrong `transfer` erases real demand.",
      ),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Stamped on written rows, and scopes WAREHOUSE code resolution to one source. Products are keyed by SKU within the organization, so it does not scope them. Leave empty to match any.",
      ),
    originEntity: z
      .string()
      .optional()
      .describe(
        "Stamped on written rows to name the source record type, e.g. `subchain`. Distinguishes ingested rules from hand-authored ones in the UI.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
